import React, { memo, ReactNode } from 'react';
import { Box, BoxProps, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoTooltip, { TooltipVariant } from 'DesignSystem/DataDisplay/InfoTooltip/InfoTooltip';
import ThemeTypography from 'DesignSystem/Primitives/Typography/ThemeTypography';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { themeColors } from 'styles/themeColors';
import Icon, { TIconName } from 'DesignSystem/Primitives/Icon/Icon';

const Title = styled('div')(({ theme }) => ({
  paddingBottom: theme.spacing(1),
}));

type TFieldWrapperVariant = 'small' | 'medium' | 'large';

const Label = styled(ThemeTypography)(() => ({
  lineHeight: 1,
}));

const Info = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  lineHeight: 1,
  fontWeight: 400,
}));

const Note = styled(Typography)(() => ({
  fontSize: 14,
}));

interface IProps {
  labelIcon?: TIconName;
  label?: string;
  subtitle?: string;
  info?: string | ReactNode;
  inverted?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  variant?: TFieldWrapperVariant;
  buttons?: ReactNode;
  tooltip?: {
    helperText: string | ReactNode;
    variant: TooltipVariant;
  };
  children: ReactNode;
}

const FieldWrapper: React.FC<IProps & BoxProps> = ({
  label,
  labelIcon,
  subtitle,
  tooltip,
  variant = 'large',
  inverted = false,
  children,
  buttons,
  required,
  info,
  fullWidth,
  ...rest
}) => {
  const labelVariant = variant !== 'small' ? 'BODY_MEDIUM_BOLD' : 'BODY_LARGE_BOLD';

  return (
    <Box width={fullWidth ? '100%' : undefined} {...rest}>
      {(label || subtitle) && (
        <Title>
          {label && (
            <Box display="flex" justifyContent="space-between">
              <Label variant={labelVariant} color={inverted ? 'WHITE' : 'BLACK'}>
                <Box display="flex" alignItems="center" component="span">
                  {labelIcon && <Icon name={labelIcon} mr={1} />}
                  <span>{label}</span>
                  {required && <span style={{ color: themeColors.primary[40] }}>&nbsp;*</span>}
                  {tooltip && (
                    <InfoTooltip
                      size={EAvailableSizes.SMALL}
                      text={tooltip.helperText}
                      variant={tooltip.variant}
                    />
                  )}
                  {buttons && <Box ml={1}>{buttons}</Box>}
                </Box>
              </Label>
              {info && <Info variant="body2">{info}</Info>}
            </Box>
          )}
          {subtitle && (
            <Note
              variant="subtitle2"
              dangerouslySetInnerHTML={{
                __html: subtitle,
              }}
            />
          )}
        </Title>
      )}
      {children}
    </Box>
  );
};

export default memo(FieldWrapper);
